import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import cx from 'classnames';
import CardTitleBox from '@home/pages/studio/components/CardTitleBox';
import { LANGUAGE_EN } from '@home/_config/config';
import { getLanguage } from '@home/utils/common';
import CustomTable from '../CustomTable';
import CommonBubble from '@home/components/CommonBubble';
import SvgIcon from '@home/components/SvgIcon';
import { StudioContext } from '@home/pages/studio/context';
import { useFactsData } from './hooks';
import EditModal from '@home/pages/studio/opinions/components/EditModal';
import { IDomEditor } from '@wangeditor/editor';
import { Icon, notification } from 'tea-component';
import { saveModuleContent } from '@home/services/studio';
import VerticalLineChart from '../VerticalLineChart';
import { getCustomStudioVerticalLineTooltip } from '../VerticalLineChart/_utils';
import { handleDownload } from '@/pages/dashboard/utils/utils';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import Abnormal from '@home/components/Abnormal';
import './index.scss';

const MetricsBreakdownTable = (props: any) => {
  const {
    className,
    style,
    titleEn = "Metrics Breakdown",
    titleCn,
    tipContentEn,
    tipContentCn,
    loadingMsgEn = 'Selecting too many languages may result in extended loading time',
    loadingMsgCn = '选择的语言数量过多可能会导致计算时间延长',
    metricsList,
    filter,
    gameInfo,
    gameMap,
    data,
    languageMap,
    chartHeight = 820,
  } = props || {};
  const { locale, formatMessage } = useIntl();
  const { editPermission } = useContext(StudioContext);
  const isEN = locale === LANGUAGE_EN;
  const { date_type: dateType, start_time: startTime, end_time: endTime, game, language } = filter || {};

  const [hoveredRow, setHoveredRow] = React.useState<string | null>(null); // 追踪悬浮行
  const [showModal, setShowModal] = useState(false);
  const [editFactsData, setEditFactsData] = useState('');
  const [loadingRowId, setLoadingRowId] = useState<string | null>(null);
  const [editLoading, setEditLoading] = useState<boolean>(false);
  const [isOverflowing, setIsOverflowing] = useState<string | null>(null);
  const [mergedSubModuleData, setMergedSubModuleData] = useState<any[]>([]);

  // 忽略图片粘贴
  const customPaste = (editor: IDomEditor, e: ClipboardEvent) => {
    const { clipboardData } = e;
    if (!clipboardData) return true;

    return ![...clipboardData.items, ...clipboardData.files].some(item => item.type.startsWith('image/'));
  };

  const {
    factsData,
    factsError,
    factsLoading,
    refreshFactsData,
    subModuleLoading,
    subModuleData
  } = useFactsData({ filter, gameInfo, setMergedSubModuleData });

  // data中获取的状态和数据
  const { isLoading, isError, isNoData, languageData } = useMemo(() => {
    const { data: tableData } = data || {};
    const { loading, error, data: metricsData } = tableData || {};
    const metricsDataList = metricsData?.metrics_list || [];
    // 语言数据
    const languageData = metricsDataList?.reduce((acc: { [x: string]: any[]; }, currentItem: { language: any; metrics: { [x: string]: { value: any; }; }; }) => {
      const { language } = currentItem;
      const newCurrentItem = {
        ...currentItem,
        originMetrics: currentItem?.metrics,
        metrics: metricsList?.map((item: { key_metric: string | number; }) => ({ key: item?.key_metric, value: currentItem?.metrics?.[item?.key_metric]?.value || "" }))
      };
      // 如果累加器中还没有这个语言的分组，就创建一个新的数组
      if (!acc[language]) {
        acc[language] = [];
      }
      // 将当前项添加到对应语言的数组中
      acc[language].push(newCurrentItem);
      return acc;
    }, {}); // 初始化累加器为一个空对象
    return {
      isLoading: loading || factsLoading,
      isError: error || factsError,
      isNoData: metricsDataList?.length === 0 && factsData?.length === 0 && metricsList?.length === 0,
      languageData,
    };
  }, [data, factsData?.length, factsError, factsLoading, metricsList]);

  // 指标名称翻译映射
  const metricNameMap = useMemo(() => {
    const metricNameMap: any = {};
    metricsList?.forEach((item: { key_metric: string | number; key_metric_desc_en: string; key_metric_desc_cn: string; }) => {
      metricNameMap[item?.key_metric] = getLanguage(isEN, item?.key_metric_desc_en, item?.key_metric_desc_cn);
    });
    return metricNameMap;
  }, [metricsList, isEN]);

  // 游戏数据
  const gameList = useMemo(() => {
    // 获取2个游戏信息
    const currentGameId = gameInfo?.edition_unified_id || '';
    const compareGameId = filter?.game || '';
    const currentGameInfo = gameMap?.[currentGameId] || {};
    const competingGameInfo = gameMap?.[compareGameId] || {};

    const gameList = [
      { ...currentGameInfo, color: '#326FF0' },
      { ...competingGameInfo, color: '#F19E55' },
    ];
    return gameList;
  }, [filter?.game, gameInfo?.edition_unified_id, gameMap]);

  const changeShowModal = () => {
    setShowModal(!showModal);
  };

  // 编辑Facts数据
  const handleSubmit = (data: string) => {
    setEditLoading(true);
    const params = {
      module_contents: [{
        id: editFactsData?.id,
        system: "studio_hub",
        type: 2,
        uid_type: "unified_id,comp_unified_id",
        uid: `${gameInfo?.edition_unified_id},${game}`,
        user_id: "",
        period: dateType,
        start_time: startTime,
        end_time: endTime,
        module: "metrics_breakdown",
        sub_module: editFactsData?.keyMetric,
        content: data,
        status: 1,
      }]
    };
    saveModuleContent?.(params).then(() => {
      setEditLoading(false);
      notification.success({
        description: formatMessage({ id: 'dbh_studio_funcom_edit_modal_success' }),
        duration: 1000,
      });
      setShowModal(false);
      setLoadingRowId(editFactsData?.keyMetric);
      refreshFactsData(editFactsData?.keyMetric);
    }).catch(() => {
      setEditLoading(false);
      notification.error({
        description: formatMessage({ id: 'dbh_studio_funcom_edit_modal_failed' }),
        duration: 1000,
      });
    });

  };

  // 处理鼠标进入Facts列事件
  const handleMouseEnter = (rowId: string, contentRef: React.RefObject<HTMLDivElement>) => {
    setHoveredRow(rowId);
    if (contentRef.current && contentRef.current.scrollHeight > contentRef.current.clientHeight) {
      setIsOverflowing(rowId);
    } else {
      setIsOverflowing(null);
    }
  };

  const columns: any = [
    {
      header: isEN ? 'Key Metrics' : '核心指标',
      key: 'keyMetrics',
      width: 200,
      align: 'center',
      fixed: "left",
      render: (record: any, rowKey: string) => {
        const data = record?.[rowKey];
        const { key_metric_desc_en: descEn, key_metric_desc_cn: descCn, metrics_en: metricsEn, metrics_cn: metricsCn } = data || {};
        const desc = getLanguage(isEN, descEn, descCn);
        const metrics = getLanguage(isEN, metricsEn, metricsCn);
        return <div className='dbh-studio-breakdown-metric'>
          <div className='dbh-studio-breakdown-metric-title'>{desc}</div>
          {metrics?.map((metric: any) => <div className='dbh-studio-breakdown-metric-item' key={metric}>{metric}</div>)}
        </div>;
      },
    },
    {
      header: isEN ? 'Facts' : '详情',
      key: 'facts',
      width: 230,
      align: 'center',
      render: (record: any, rowKey: string) => {
        const data = record?.[rowKey];
        const rowId = `${data?.keyMetric}`; // 生成唯一 ID
        // const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '');
        const contentRef = React.createRef<HTMLDivElement>();

        return subModuleLoading && loadingRowId === rowId ? (
          <div className="dbh-studio-breakdown-facts-loading">
            <Icon size="l" type="loading" className="loading-icon" />
          </div>
        ) : (
          <div
            className={cx('dbh-studio-breakdown-facts', { 'dbh-studio-breakdown-facts-editable': editPermission })}
            onMouseEnter={() => handleMouseEnter(rowId, contentRef)}
            onMouseLeave={() => setHoveredRow(null)}
          >
            <div className="dbh-studio-breakdown-facts-edit">
              {editPermission && hoveredRow === rowId && (
                <CommonBubble content={getLanguage(isEN, "Edit", "编辑")}>
                  <span
                    className="dbh-studio-breakdown-facts-edit-icon"
                    onClick={() => {
                      setShowModal(true);
                      setEditFactsData(data);
                    }}
                  >
                    <SvgIcon type="edit-icon" />
                  </span>
                </CommonBubble>
              )}
            </div>
            {isOverflowing === rowId ? (
              <CommonBubble content={ <div dangerouslySetInnerHTML={{ __html: data?.content}}/>}>
                <div
                  className="dbh-studio-breakdown-facts-summary"
                  ref={contentRef}
                  dangerouslySetInnerHTML={{ __html: data?.content || '-' }}
                ></div>
              </CommonBubble>
            ) : (
              <div
                className="dbh-studio-breakdown-facts-summary"
                ref={contentRef}
                dangerouslySetInnerHTML={{ __html: data?.content || '-' }}
              ></div>
            )}
          </div>
        );
      },
    },
    ...language?.map((item: any, index: number) => ({
      header: languageMap?.[item]?.name || "",
      key: item,
      width: 160,
      align: 'center',
      render: (record: any, rowKey: string) => {
        const dataSource = record?.[rowKey] || [];
        const series = gameList?.map((item) => ({ name: item?.unified_edition_id, formatter: 'oneDecimal' }));
        let isNoData = true;
        dataSource?.map((item: { metrics: any[]; }) => {
          item?.metrics?.map(metric => {
            if (metric?.value) {
              isNoData = false;
            }
          });
        });

        // 根据id排序
        const sortData: any[] = [];
        gameList?.map((item) => {
          const metric = dataSource?.find((data: { unified_edition_id: any; }) => data?.unified_edition_id === item?.unified_edition_id);
          if (metric) {
            sortData.push(metric);
          }
        });

        return <Abnormal
          isLoading={false}
          isNoData={isNoData}
          nodataMsg={isEN ? "No data" : "无数据"}
          component={
            <div key={JSON.stringify(rowKey + isEN)}>
              <VerticalLineChart
                style={{ width: '100%', height: chartHeight ? `${chartHeight}px` : "820px" }}
                className="dbh-studio-breakdown-facts-chart"
                dataSource={sortData}
                series={series}
                tooltip={{
                  // 需求上length最多为9
                  confine: index <= (language?.length - 1) / 2,
                  formatter: (params) => getCustomStudioVerticalLineTooltip(params, {
                    nameHeader: languageMap?.[item]?.name || "",
                    valueHeader: '游戏名称',
                    formatter: 'oneDecimal',
                    gameList,
                    metricNameMap,
                    className: 'dbh-studio-benchmark-breakdown-tooltip'
                  }),
                }}
              />
            </div>
          }
        />;
      }
    })),
  ];

  useEffect(() => {
    // 合并新旧子模块数据
    // 使用Map结构确保每个sub_module只保留最新的数据
    setMergedSubModuleData((prevData) => {
      const updatedMap = new Map(prevData.map((item) => [item.sub_module, item]));
      subModuleData?.forEach((item) => {
        updatedMap.set(item.sub_module, item);
      });
      return Array.from(updatedMap.values());
    });
  }, [subModuleData]);


  const records = useMemo(() => {
    // 将子模块数据转换为Map结构以便快速查找
    const subModuleMap = new Map(mergedSubModuleData.map((item) => [item.sub_module, item]));

    return (
      metricsList?.map((metric: any) => {
        // 查找对应的facts数据和更新后的子模块数据
        const fact = factsData?.find((fact: any) => fact.sub_module === metric.key_metric) || {};
        const updatedFact = subModuleMap.get(metric.key_metric) || {};

        // 合并数据并返回记录对象
        return {
          keyMetrics: metric,
          facts: { ...fact, ...updatedFact, keyMetric: metric.key_metric },
          ...languageData,
        };
      }) || []
    );
  }, [metricsList, factsData, mergedSubModuleData, languageData]);

  // 配置化的标题、提示和加载文本
  const { title, titleTip, loadingMsg } = useMemo(() => {
    const title = getLanguage(isEN, titleEn, titleCn);
    const titleTip = getLanguage(isEN, tipContentEn, tipContentCn);
    const loadingMsg = getLanguage(isEN, loadingMsgEn, loadingMsgCn);
    return {
      title,
      titleTip,
      loadingMsg,
    };
  }, [isEN, loadingMsgCn, loadingMsgEn, tipContentCn, tipContentEn, titleCn, titleEn]);


  const download = () => {
    // 组装表头
    const tableHeaders = [
      { key: 'language', header: getLanguage(isEN, 'Language', '语言') },
      { key: 'game', header: getLanguage(isEN, 'Game', '游戏') },
    ];
    const metricsHeader = records?.map((record: any) => {
      const { key_metric, key_metric_desc_cn, key_metric_desc_en } = record?.keyMetrics || {};
      return {
        key: key_metric,
        header: getLanguage(isEN, key_metric_desc_en, key_metric_desc_cn)
      };
    }) || [];
    tableHeaders.push(...metricsHeader);

    // 组装数据
    // 获取2个游戏信息
    const currentGameId = gameInfo?.edition_unified_id || '';
    const compareGameId = filter?.game || '';
    const currentGameInfo = gameMap?.[currentGameId] || {};
    const competingGameInfo = gameMap?.[compareGameId] || {};
    const allData = records?.[0] || {};
    const tableData: any[] = [];
    language?.forEach((lang: string) => {
      // 一种语言2条数据
      const langInfo = languageMap[lang] || {};
      const currentData: any = { language: langInfo?.name, game: currentGameInfo?.name };
      const competingData: any = { language: '', game: competingGameInfo?.name };
      const languageData = allData?.[lang] || [];
      const currentDataItem = languageData.find((item: any) => item?.unified_edition_id === currentGameId) || {};
      const competingDataItem = languageData.find((item: any) => item?.unified_edition_id === compareGameId) || {};
      metricsHeader.forEach((item: { key: string; }) => {
        currentData[item.key] = dataFormat({ value: currentDataItem?.originMetrics?.[item.key]?.value, formatRule: 'oneDecimal' });
        competingData[item.key] = dataFormat({ value: competingDataItem?.originMetrics?.[item.key]?.value, formatRule: 'oneDecimal' });
      });
      tableData.push(...[currentData, competingData]);
    });

    const tableConfig = {
      option: tableHeaders,
      downloadData: tableData
    };
    handleDownload(tableConfig, title);
  };

  return (
    <CardTitleBox
      title={title}
      titleTip={titleTip}
      className={cx('dbh-studio-social-index-breakdown', className)}
      style={style}
      normalConfig={{
        isLoading,
        isError,
        isNoData,
        loadingMsg,
      }}
      rightSolt={<i className="dbh-studio-social-index-breakdown-down-btn" onClick={download}><SvgIcon type="download" /></i>}
    >
      <CustomTable
        className="dbh-studio-breakdown-table"
        columns={columns}
        records={records}
        addons={{
          rowSpan: (columnIndex: number, recordIndex: number) => {
            if (columnIndex >= 2) {
              if (recordIndex === 0) return 5;
              if (recordIndex < 5) return 0;
              return 1;
            }
            return 1;
          },
        }}
      />
      <EditModal
        title={formatMessage({ id: 'dbh_studio_edit_facts' })}
        loading={editLoading}
        closeModal={changeShowModal}
        isVisible={showModal}
        customPaste={customPaste}
        data={editFactsData?.content}
        handleSubmit={handleSubmit}
        handleCancel={showModal}
        handelCancel={changeShowModal}
        // 解决重新打开modal编辑器报错问题，editor未实例化
        destroyOnClose={false}
      />
    </CardTitleBox>
  );

};

export default MetricsBreakdownTable;